import React, { useState, useEffect, useCallback } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import Layout from '../components/Layout';
import { expenseAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const CATEGORIES = ['Rent','Electricity','Water','Internet','Phone','Salary','Transport','Packaging','Maintenance','Marketing','Insurance','Tax','General','Other'];
const PAYMENT_MODES = ['Cash','Card','UPI','Bank Transfer','Cheque','Credit'];

const fmtDate = d => formatDateDMY(d, '');
const fmtNum = v => (parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ── Expense Modal ── */
const ExpenseModal = ({ expense, onClose, onSaved }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const req = { color: '#ef4444', marginLeft: 2 };

  const [form, setForm] = useState({
    category: expense?.category || 'General',
    description: expense?.description || '',
    amount: expense?.amount ?? '',
    expense_date: expense?.expense_date ? expense.expense_date.split('T')[0] : new Date().toISOString().split('T')[0],
    payment_mode: expense?.payment_mode || 'Cash',
    reference: expense?.reference || '',
    notes: expense?.notes || '',
    paid_to: expense?.paid_to || '',
    cheque_no: expense?.cheque_no || '',
    paid_by: expense?.paid_by || '',
  });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.description.trim()) { alert('Description is required'); return; }
    if (!parseFloat(form.amount)) { alert('Amount must be greater than 0'); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (expense) await expenseAPI.update(expense.id, payload);
      else await expenseAPI.create(payload);
      onSaved();
    } catch (err) { alert(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: cardBg, borderRadius: 16, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)` }} />
        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          <h3 style={{ color: darkMode ? '#F1F5F9' : '#0F172A', fontSize: 18, fontWeight: 800, marginBottom: 20, margin: 0, marginBottom: 20 }}>
            {expense ? '✏️ Edit Expense' : '➕ New Expense'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <div><label style={lbl}>Category<span style={req}>*</span></label>
              <select style={inp} value={form.category} onChange={set('category')}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Amount (₹)<span style={req}>*</span></label><input style={inp} type="number" min="0" step="0.01" required value={form.amount} onChange={set('amount')} placeholder="0.00" /></div>
            <div><label style={lbl}>Date<span style={req}>*</span></label><StrictDateInput style={inp}  required value={form.expense_date} onChange={set('expense_date')} /></div>
            <div><label style={lbl}>Payment Mode</label>
              <select style={inp} value={form.payment_mode} onChange={set('payment_mode')}>
                {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Description<span style={req}>*</span></label><input style={inp} required value={form.description} onChange={set('description')} placeholder="What was this expense for?" /></div>
            <div><label style={lbl}>Paid To</label><input style={inp} value={form.paid_to} onChange={set('paid_to')} placeholder="Vendor / Person name" /></div>
            <div><label style={lbl}>Paid By</label><input style={inp} value={form.paid_by} onChange={set('paid_by')} placeholder="Staff / Account" /></div>
            <div><label style={lbl}>Reference / Bill No.</label><input style={inp} value={form.reference} onChange={set('reference')} placeholder="Bill #, Receipt #" /></div>
            <div><label style={lbl}>Cheque No.</label><input style={inp} value={form.cheque_no} onChange={set('cheque_no')} placeholder="If paid by cheque" /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Notes</label><textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={form.notes} onChange={set('notes')} placeholder="Additional notes…" /></div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '10px 24px', background: saving ? '#94A3B8' : acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? '⏳ Saving…' : expense ? '💾 Update' : '💾 Save Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Main Expenses Page ── */
export const ExpensesContent = () => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' };

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [stats, setStats] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filterCategory) params.category = filterCategory;
      const r = await expenseAPI.list(params);
      setExpenses(r.data.expenses || []);
      setTotalPages(r.data.pages || 1);
    } catch { setExpenses([]); }
    setLoading(false);
  }, [page, filterCategory]);

  const fetchStats = useCallback(async () => {
    try { const r = await expenseAPI.stats(); setStats(r.data); } catch {}
  }, []);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await expenseAPI.delete(deleteId); setDeleteId(null); fetchExpenses(); fetchStats(); } catch (err) { alert('Delete failed'); }
  };

  const filtered = search ? expenses.filter(e => [e.description, e.paid_to, e.category, e.reference, e.notes].some(v => v && v.toLowerCase().includes(search.toLowerCase()))) : expenses;
  const totalAmount = filtered.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  return (
    <div style={{ padding: 'clamp(12px, 3vw, 28px)', maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ ...pg, fontSize: 22, fontWeight: 800, margin: 0 }}>💸 Expenses</h1>
            <p style={{ ...muted, fontSize: 13, margin: '4px 0 0' }}>Track and manage all your business expenses</p>
          </div>
          <button onClick={() => { setEditExpense(null); setShowModal(true); }}
            style={{ padding: '10px 20px', background: acc, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            ➕ Add Expense
          </button>
        </div>

        {/* Stats cards */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div style={{ ...card, padding: 16 }}>
              <div style={{ ...muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Total Expenses</div>
              <div style={{ ...pg, fontSize: 20, fontWeight: 800 }}>₹{fmtNum(stats.total_expenses || 0)}</div>
            </div>
            <div style={{ ...card, padding: 16 }}>
              <div style={{ ...muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Categories</div>
              <div style={{ ...pg, fontSize: 20, fontWeight: 800 }}>{(stats.by_category || []).length}</div>
            </div>
            <div style={{ ...card, padding: 16 }}>
              <div style={{ ...muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>This Month</div>
              <div style={{ ...pg, fontSize: 20, fontWeight: 800 }}>₹{fmtNum((stats.monthly || []).find(m => m.month === new Date().toISOString().slice(0,7))?.total || 0)}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ ...card, padding: 14, marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ ...inp, flex: 1, minWidth: 200 }} value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="🔍 Search expenses…" />
          <select style={{ ...inp, width: 160 }} value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1); }}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        {/* Table */}
        <div style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', ...muted }}>Loading expenses…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>💸</div>
              <div style={{ ...pg, fontWeight: 700 }}>No expenses found</div>
              <div style={{ ...muted, fontSize: 13 }}>Add your first expense to start tracking</div>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                  <thead>
                    <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                      {['Date', 'Category', 'Description', 'Paid To', 'Mode', 'Amount', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(exp => (
                      <tr key={exp.id} style={{ borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                        <td style={{ padding: '10px 14px', fontSize: 13, ...pg, whiteSpace: 'nowrap' }}>{fmtDate(exp.expense_date)}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: darkMode ? '#1e3a5f' : '#eff6ff', color: acc }}>{exp.category}</span>
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: 13, ...pg, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exp.description}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, ...muted }}>{exp.paid_to || '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, ...muted }}>{exp.payment_mode}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, color: '#dc2626', fontSize: 13, whiteSpace: 'nowrap' }}>₹{fmtNum(exp.amount)}</td>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                          <button onClick={() => { setEditExpense(exp); setShowModal(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, marginRight: 6 }} title="Edit">✏️</button>
                          <button onClick={() => setDeleteId(exp.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }} title="Delete">🗑️</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}`, background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                      <td colSpan={5} style={{ padding: '10px 14px', fontWeight: 700, ...pg, textAlign: 'right' }}>Page Total:</td>
                      <td style={{ padding: '10px 14px', fontWeight: 800, color: '#dc2626', fontSize: 14 }}>₹{fmtNum(totalAmount)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ padding: '12px 14px', display: 'flex', justifyContent: 'center', gap: 8 }}>
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', background: darkMode ? '#0F172A' : '#F8FAFC', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 6, cursor: page <= 1 ? 'not-allowed' : 'pointer', ...pg }}>← Prev</button>
                  <span style={{ ...muted, fontSize: 13, padding: '6px 10px' }}>Page {page} of {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', background: darkMode ? '#0F172A' : '#F8FAFC', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 6, cursor: page >= totalPages ? 'not-allowed' : 'pointer', ...pg }}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>

      {/* Expense Modal */}
      {showModal && <ExpenseModal expense={editExpense} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); fetchExpenses(); fetchStats(); }} />}

      {/* Delete Confirmation */}
      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: darkMode ? '#1E293B' : '#fff', borderRadius: 14, padding: 24, maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>⚠️</div>
            <h3 style={{ ...pg, marginBottom: 8 }}>Delete Expense?</h3>
            <p style={{ ...muted, fontSize: 13, marginBottom: 20 }}>This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setDeleteId(null)} style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, cursor: 'pointer', ...pg }}>Cancel</button>
              <button onClick={handleDelete} style={{ padding: '9px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Expenses = () => (
  <Layout><ExpensesContent /></Layout>
);

export default Expenses;
